import axios from 'axios';
import { jwtDecode, type JwtPayload } from 'jwt-decode';
import { AnonymousTokenNotAllowedError } from '../errors/AnonymousTokenNotAllowedError';
import { getBaseUrl } from './Configuration';
import { getStore } from './env';
import {
  windowPresent, getFromLocalStorage, LS_KEYS, setInLocalStorage,
} from './window';

export interface AnonymousCoamIdentity {
  anonymousToken: string;
  anonymousId: string;
  validationKey: string;
}

export interface PrintdealAuthTokenPayload extends JwtPayload {
  anonymous?: boolean;
  account?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  customer?: {
    id: string;
  };
  storeKey: string;
}

const decodeJTW = (token: string) => jwtDecode<JwtPayload>(token);

const persistAuthToken = (token: string) => {
  setInLocalStorage(LS_KEYS.AUTH_TOKEN, token);

  if (!windowPresent) {
    return;
  }

  window.dispatchEvent(new StorageEvent('storage', {
    key: LS_KEYS.AUTH_TOKEN,
    newValue: token,
  }));
};
const persistCOAMToken = (token: string) => setInLocalStorage(LS_KEYS.ANON_COAM_IDENTITY, token);
const checkAuthToken = (): string | undefined => getFromLocalStorage(LS_KEYS.AUTH_TOKEN);
const checkCOAMToken = (): string | undefined => getFromLocalStorage(LS_KEYS.ANON_COAM_IDENTITY);

let pendingCreateTokenRequest: Promise<string> | undefined;

/**
 * Get an anonymous Printdeal token.
 * The auth saga and HttpClientFactory call this outside React, so this cannot use the
 * createToken hook. The request matches that hook, and parallel calls share one request.
 */
const getAnonymousToken = async (): Promise<string> => {
  // Somehow Gatsby tries to fetch a token during build time,
  // which will result in an error, causing every incremental
  // build to rebuild all HTML pages instead of only the
  // updated ones. This line prevents that.
  if (!windowPresent) return '';

  if (pendingCreateTokenRequest) {
    return pendingCreateTokenRequest;
  }

  pendingCreateTokenRequest = axios.get<{ token: string }>('/createToken', {
    baseURL: getBaseUrl('AUTHENTICATION_API'),
    params: {
      storeKey: getStore(),
    },
    withCredentials: true,
    timeout: 10_000,
  }).then(({ data }) => data.token);

  try {
    return await pendingCreateTokenRequest;
  } finally {
    pendingCreateTokenRequest = undefined;
  }
};

/**
 * Get an anonymous COAM identity
 * @see https://cimpress-support.atlassian.net/wiki/spaces/CI/pages/537165901/Anonymous+Authentication
 */
const getAnonymousCOAMIdentity = async (
  identity?: AnonymousCoamIdentity,
): Promise<AnonymousCoamIdentity | undefined> => {
  // Somehow Gatsby tries to fetch a token during build time,
  // which will result in an error, causing every incremental
  // build to rebuild all HTML pages instead of only the
  // updated ones. This line prevents that.
  if (!windowPresent) return undefined;

  const { data } = await axios.post<AnonymousCoamIdentity>(
    `${process.env.GATSBY_AUTH_LAYER_ENDPOINT}/anon-coam-identity/`,
    identity,
  );

  return data;
};

/**
 * Check if token is expired
 */
const isDecodedTokenExpired = (decodedToken: PrintdealAuthTokenPayload | JwtPayload): boolean => {
  if (!decodedToken.exp) {
    return true;
  }

  // If the expiry time is before the current time, it's expired
  return Boolean(decodedToken.exp <= Math.floor(new Date().getTime() / 1000));
};

const decodePrintdealToken = (token: string): PrintdealAuthTokenPayload => jwtDecode<PrintdealAuthTokenPayload>(token);

const getDecodedToken = (): PrintdealAuthTokenPayload | undefined => {
  const token = checkAuthToken();
  if (token) {
    return decodePrintdealToken(token);
  }
  return undefined;
};

/**
 * Check if user is authenticated
 */
const isAuthenticated = (token = checkAuthToken()): boolean => {
  if (!token) {
    return false;
  }

  const decodedToken = decodePrintdealToken(token);

  if (!decodedToken) {
    return false;
  }

  if ('anonymous' in decodedToken || isDecodedTokenExpired(decodedToken)) {
    return false;
  }

  return true;
};

/**
 * If a customers login token is expired but he makes a request that is allowed for non logged in users
 * it creates automatically a new anonymousToken
 * This behavior can be disabled by setting `blockAnonymous` to `true` in the request config
 * @returns {string}
 */
const getAuthToken = async (allowAnonymous: boolean): Promise<string | undefined> => {
  const token = getDecodedToken();

  if (!token || isDecodedTokenExpired(token) || !token.storeKey) {
    if (!allowAnonymous) {
      throw new AnonymousTokenNotAllowedError('Token missing or expired; anonymous fallback not allowed');
    }

    const anonymousToken = await getAnonymousToken();
    persistAuthToken(anonymousToken);
    return anonymousToken;
  }

  // Handle potential race conditions that send an anonymous token for a login-restricted endpoint
  if (!allowAnonymous && token.anonymous) {
    throw new AnonymousTokenNotAllowedError('Anonymous token used for logged-in call');
  }

  return checkAuthToken();
};

const getCOAMToken = async (): Promise<AnonymousCoamIdentity | undefined> => {
  const COAMToken = checkCOAMToken();

  if (!COAMToken) {
    const anonymousToken = await getAnonymousCOAMIdentity();
    persistCOAMToken(JSON.stringify(anonymousToken));
    return anonymousToken;
  }

  const token = COAMToken && jwtDecode(COAMToken) as JwtPayload;

  if (token && COAMToken && isDecodedTokenExpired(token)) {
    const identity = JSON.parse(COAMToken) as AnonymousCoamIdentity;
    const anonymousToken = await getAnonymousCOAMIdentity(identity);
    persistCOAMToken(JSON.stringify(anonymousToken));
    return anonymousToken;
  }

  if (COAMToken) {
    return JSON.parse(COAMToken) as AnonymousCoamIdentity;
  }

  return undefined;
};

export {
  persistAuthToken,
  getDecodedToken,
  isDecodedTokenExpired,
  isAuthenticated,
  getAuthToken,
  getAnonymousCOAMIdentity,
  getAnonymousToken,
  decodeJTW,
  decodePrintdealToken,
  getCOAMToken,
};
